import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import { providerApi } from '../../services/api';
import { formatPrice } from '../../utils/currencyUtils';

// Whole amounts read better as "₪1800" than "₪1800.00"; keep cents when there are some.
const money = (amount, currencyCode) =>
  formatPrice(amount, currencyCode, Number.isInteger(Number(amount)) ? 0 : 2);

/**
 * Overview card showing the commission a STORE provider pays. Self-contained: it loads
 * GET /providers/me/commission and renders loading / error / flat / tiered / top-tier states.
 * Tiered: this month's rate (from last month's sales), then next month's rate if this month's
 * sales already change it, then how much more to sell this month to reach the next tier.
 * The parent decides visibility (STORE accounts only).
 */
export default function CommissionCard() {
  const { t } = useTranslation();
  const [commission, setCommission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await providerApi.getMyCommission();
        if (!cancelled) setCommission(data);
      } catch (err) {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const tiered = commission?.mode === 'TIERED';
  const next = tiered ? commission.nextTier : null;
  const currency = commission?.currencyCode;
  // This month's sales already put next month in a different tier than this month's.
  const nextMonthRate = tiered ? commission.nextMonthRatePercent : null;
  const nextMonthDiffers = nextMonthRate != null && Number(nextMonthRate) !== Number(commission.ratePercent);

  return (
    <Paper
      elevation={0}
      data-testid="commission-card"
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5, mb: 3 }}
    >
      <Typography
        variant="caption"
        sx={{
          color: 'text.disabled', fontWeight: 700, letterSpacing: '0.08em',
          textTransform: 'uppercase', fontSize: '0.68rem', display: 'block', mb: 1.5,
        }}
      >
        {t('overview.commissionTitle')}
      </Typography>

      {loading && <CircularProgress size={20} aria-label={t('overview.commissionTitle')} />}

      {!loading && failed && (
        <Typography variant="body2" color="text.secondary">{t('overview.commissionLoadFailed')}</Typography>
      )}

      {!loading && !failed && commission && (
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {tiered
              ? t('overview.commissionTiered', { rate: commission.ratePercent })
              : t('overview.commissionFlat', { rate: commission.ratePercent })}
          </Typography>

          {nextMonthDiffers && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {t('overview.commissionNextMonthRate', { rate: nextMonthRate })}
            </Typography>
          )}

          {next && (
            <>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {t('overview.commissionNextTier', {
                  amount: money(next.remainingThisMonth, currency),
                  nextRate: next.ratePercent,
                })}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {t('overview.commissionNextTierThreshold', {
                  threshold: money(next.threshold, currency),
                  nextRate: next.ratePercent,
                })}
              </Typography>
            </>
          )}

          {tiered && commission.lastMonthVolume != null && commission.thisMonthVolume != null && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {t('overview.commissionVolumes', {
                last: money(commission.lastMonthVolume, currency),
                current: money(commission.thisMonthVolume, currency),
              })}
            </Typography>
          )}
        </Box>
      )}
    </Paper>
  );
}
